import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import {
  getDocumentPromotionStatus,
  promoteDocument as promoteDocumentRequest,
  demoteDocument as demoteDocumentRequest,
} from 'modules/FileManagerV2/api/promotionService';
import { MEDICAL_RECORD_PERMISSION_TOAST, PROMOTION_QUERY_KEY } from 'modules/FileManagerV2/constants/medicalRecordPromotion';

const PERMISSION_DENIED_STATUS = 403;

/**
 * Fetches HealthLake promotion status for a File Manager document and exposes promote/demote mutations.
 * @param {string} fileId
 * @param {{ enabled?: boolean }} [options]
 */
const useDocumentPromotion = (fileId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { userDetails } = useUserDetailsContext();
  const { addToast } = useToast();
  const firmUserId = userDetails?.firm_user_id;

  const handlePermissionError = (error) => {
    if (error?.response?.status === PERMISSION_DENIED_STATUS) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, MEDICAL_RECORD_PERMISSION_TOAST);
    }
  };

  const promotionQuery = useQuery([PROMOTION_QUERY_KEY, fileId], () => getDocumentPromotionStatus({ fileId, firmUserId }), {
    staleTime: 30000,
    enabled: enabled && !!fileId && !!firmUserId,
    retry: 3,
    select: (data) => data,
  });

  const promoteMutation = useMutation(({ triggerSource }) => promoteDocumentRequest({ fileId, firmUserId, triggerSource }), {
    onSuccess: () => {
      queryClient.invalidateQueries([PROMOTION_QUERY_KEY, fileId]);
    },
    onError: handlePermissionError,
  });

  const demoteMutation = useMutation(() => demoteDocumentRequest({ fileId, firmUserId }), {
    onSuccess: () => {
      queryClient.invalidateQueries([PROMOTION_QUERY_KEY, fileId]);
    },
    onError: handlePermissionError,
  });

  return {
    promotionStatus: promotionQuery.data?.promotionStatus ?? 'not_promoted',
    promotedAt: promotionQuery.data?.promotedAt ?? null,
    promotionSource: promotionQuery.data?.promotionSource ?? null,
    promotedBy: promotionQuery.data?.promotedBy ?? null,
    promotionLog: promotionQuery.data?.promotionLog ?? [],
    promoteDocument: promoteMutation.mutate,
    demoteDocument: demoteMutation.mutate,
    isPromoting: promoteMutation.isLoading,
    isDemoting: demoteMutation.isLoading,
    isLoading: promotionQuery.isLoading,
  };
};

export default useDocumentPromotion;
